import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { formatRupiah, formatDateShort, STATUS_LABEL, STATUS_ORDER } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Search, Download } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState([]);
  const status = params.get("status") || "";

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const url = status ? `/orders?status=${status}` : "/orders";
      const { data } = await api.get(url);
      setOrders(data);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); setSelectedIds([]); }, [status]);

  const updateStatus = async (e, id, currentStatus) => {
    e.stopPropagation();
    const currentIndex = STATUS_ORDER.indexOf(currentStatus);
    if (currentIndex >= 0 && currentIndex < STATUS_ORDER.length - 1) {
      const nextStatus = STATUS_ORDER[currentIndex + 1];
      try {
        await api.put(`/orders/${id}`, { status: nextStatus });
        toast.success(`Status diubah menjadi ${STATUS_LABEL[nextStatus]}`);
        fetchOrders();
      } catch (err) {
        toast.error("Gagal mengubah status");
      }
    }
  };

  const filtered = orders.filter((o) => {
    if (!status && o.status === "diterima") return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(t) ||
      o.customer?.name?.toLowerCase().includes(t) ||
      o.customer?.phone?.toLowerCase().includes(t)
    );
  });

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length && filtered.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(o => o.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(x => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleDownloadExcel = () => {
    if (selectedIds.length === 0) {
      toast.error("Pilih minimal 1 pesanan untuk di download");
      return;
    }
    const ids = selectedIds.join(',');
    window.open(`${API}/orders/export/xlsx?ids=${ids}`, '_blank');
  };

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]" data-testid="orders-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <div className="clf-label mb-2">Pesanan</div>
          <h1 className="text-3xl tracking-tight font-medium">Daftar Pesanan</h1>
          <p className="text-sm text-stone-500 mt-1">
            Semua orderan kemeja, jaket & seragam.
          </p>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.5} />
            <input
              data-testid="orders-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari no order / customer / telp..."
              className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-[#D9CDB5] focus:border-stone-900 focus:outline-none rounded-sm"
            />
          </div>

          <div className="flex items-center gap-1 border border-[#D9CDB5] bg-white p-0.5">
            <button
              onClick={() => setParams({})}
              data-testid="filter-all"
              className={`px-3 py-1.5 text-xs font-medium ${!status ? "bg-[#16202B] text-white" : "text-stone-600 hover:bg-stone-100"}`}
            >
              Semua
            </button>
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                onClick={() => setParams({ status: s })}
                data-testid={`filter-${s}`}
                className={`px-3 py-1.5 text-xs font-medium ${status === s ? "bg-[#16202B] text-white" : "text-stone-600 hover:bg-stone-100"}`}
              >
                {STATUS_LABEL[s].split(" ")[0]}
              </button>
            ))}
          </div>
        </div>
        
        <button
          onClick={handleDownloadExcel}
          data-testid="export-xlsx-btn"
          className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-800 flex items-center gap-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={selectedIds.length === 0}
        >
          <Download className="h-4 w-4" strokeWidth={1.5} /> Download ke Excel
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5DCC7]">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="border-b border-[#E5DCC7] bg-stone-50">
            <tr>
              <th className="px-4 py-3 w-10 text-center">
                <input 
                  type="checkbox" 
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  onChange={toggleSelectAll}
                  className="rounded border-stone-300 text-emerald-600 focus:ring-emerald-600"
                />
              </th>
              <th className="text-left clf-label px-4 py-3">No. Order</th>
              <th className="text-left clf-label px-4 py-3">Customer</th>
              <th className="text-left clf-label px-4 py-3">Tgl Masuk</th>
              <th className="text-left clf-label px-4 py-3">Items</th>
              <th className="text-right clf-label px-4 py-3">Total</th>
              <th className="text-left clf-label px-4 py-3">Status</th>
              <th className="text-right clf-label px-4 py-3">Bayar</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-stone-500">Memuat...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-stone-500">
                Belum ada pesanan.
              </td></tr>
            ) : (
              filtered.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-stone-100 last:border-0 hover:bg-stone-50 cursor-pointer"
                  data-testid={`order-row-${o.id}`}
                >
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={selectedIds.includes(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      className="rounded border-stone-300 text-emerald-600 focus:ring-emerald-600"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-stone-900 font-medium">
                    <Link to={`/orders/${o.id}`} className="hover:text-[#A63D30]">
                      {o.order_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-stone-900">{o.customer?.name || "—"}</div>
                    <div className="text-[11px] text-stone-500">{o.customer?.phone}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-stone-700">
                    {formatDateShort(o.tanggal_masuk)}
                  </td>
                  <td className="px-4 py-3 text-stone-700">
                    {o.items?.length || 0} item
                    <span className="text-[11px] text-stone-500 ml-1">
                      ({o.items?.reduce((s, i) => s + (i.quantity || 0), 0)} pcs)
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-medium">
                    {formatRupiah(o.total)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={o.status || "masuk"} />
                      {STATUS_ORDER.indexOf(o.status || "masuk") < STATUS_ORDER.length - 1 && (
                        <button
                          onClick={(e) => updateStatus(e, o.id, o.status || "masuk")}
                          className="text-[10px] bg-stone-100 hover:bg-stone-200 text-stone-600 px-2 py-1 rounded"
                        >
                          → {STATUS_LABEL[STATUS_ORDER[STATUS_ORDER.indexOf(o.status || "masuk") + 1]].split(" ")[0]}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs font-medium ${
                      o.payment_status === "lunas" ? "text-emerald-700" :
                      o.payment_status === "dp" ? "text-amber-700" : "text-stone-500"
                    }`}>
                      {o.payment_status.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
